// app.routes.ts
import { Routes } from '@angular/router';
import { BlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { AppUnauthorizedComponent } from './pages/authentication/unauthorized/unauthorized.component';
import { TokenGuard } from './guard/tokenAuth.guard';

export const routes: Routes = [
  // ========== ROTAS PÚBLICAS (SEM GUARDS) - DEVEM VIR PRIMEIRO ==========
  
  // Rota pública alternativa para convite (sem /public)
  {
    path: 'invite/:token',
    component: BlankComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/public/client-invite/client-invite.component').then(
            (m) => m.ClientInviteComponent
          ),
      },
    ],
  },

  // Rotas públicas com /public
  {
    path: 'public',
    component: BlankComponent,
    children: [
      {
        path: 'labdips/:token',
        loadComponent: () =>
          import('./pages/public/labdips/public-labdip.component').then(
            (m) => m.PublicLabdipComponent
          ),
      },
      {
        path: 'bulks/:token',
        loadComponent: () =>
          import('./pages/public/bulks/public-bulks.component').then(
            (m) => m.PublicBulksComponent
          ),
      },
      {
        path: 'sourcing/:token',
        loadComponent: () =>
          import('./pages/public/sourcing/public-sourcing.component').then(
            (m) => m.PublicSourcingComponent
          ),
      },
      {
        path: 'protos/:token',
        loadComponent: () =>
          import('./pages/public/protos/public-proto.component').then(
            (m) => m.PublicProtoComponent
          ),
      },
      {
        path: 'trims/:token',
        loadComponent: () =>
          import('./pages/public/trims/public-trims.component').then(
            (m) => m.PublicTrimsComponent
          ),
      },
      {
        path: 'print-embroidery/:token',
        loadComponent: () =>
          import('./pages/public/print-embroidery/public-print-embroidery.component').then(
            (m) => m.PublicPrintEmbroideryComponent
          ),
      },
      {
        path: 'products/:token',
        loadComponent: () =>
          import('./pages/public/products/public-product.component').then(
            (m) => m.PublicProductComponent
          ),
      },
      {
        path: 'invite/:token',
        loadComponent: () =>
          import('./pages/public/client-invite/client-invite.component').then(
            (m) => m.ClientInviteComponent
          ),
      },
    ],
  },

  // Safety-net redirects for in-app deep links that were sent out with the wrong
  // URL prefix (legacy / backend bug: emails generated `/public/protos?openProto=<id>`
  // instead of the documented `/protos?openProto=<id>`). The public token-based pages
  // require a `:token` path segment, so `/public/protos` with only a query param does
  // not match the public child routes and would fall through to the auth catch-all
  // (losing the `openProto` query in the process and never opening the dialog).
  //
  // We redirect those URLs to the authenticated equivalent. Query params are preserved
  // by Angular's `redirectTo` by default, so `?openProto=213` survives the bounce.
  // `pathMatch: 'full'` is REQUIRED so this does NOT swallow real public token URLs
  // like `/public/protos/abc-123` (those still hit the public component above).
  //
  // See `docs/BACKEND-PROTO-EMAIL-IN-APP-DELIVERY-2026-05-11.md` § 11.
  { path: 'public/protos', pathMatch: 'full', redirectTo: '/protos' },
  { path: 'public/labdips', pathMatch: 'full', redirectTo: '/labdips' },
  { path: 'public/bulks', pathMatch: 'full', redirectTo: '/bulks' },
  { path: 'public/sourcing', pathMatch: 'full', redirectTo: '/sourcing' },
  { path: 'public/trims', pathMatch: 'full', redirectTo: '/trims' },
  { path: 'public/print-embroidery', pathMatch: 'full', redirectTo: '/print-embroidery' },
  { path: 'public/products', pathMatch: 'full', redirectTo: '/products' },

  // Rotas de autenticação (sem guards)
  {
    path: 'authentication',
    component: BlankComponent,
    loadChildren: () =>
      import('./pages/authentication/authentication.routes').then(
        (m) => m.AuthenticationRoutes
      ),
  },

  // Rota pública para reset de password (deve vir ANTES das rotas protegidas)
  {
    path: 'password/:token',
    component: BlankComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/authentication/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent
          ),
      },
    ],
  },

  {
    path: 'unauthorized',
    component: AppUnauthorizedComponent,
  },

  // ========== ROTAS PROTEGIDAS (COM TOKEN GUARD) - DEVEM VIR DEPOIS ==========
  
  {
    path: '',
    component: FullComponent,
    canActivate: [TokenGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/tables/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
      {
        path: 'labdips',
        loadComponent: () =>
          import('./pages/tables/labdips/labdips.component').then(
            (m) => m.LabdipsComponent
          ),
      },
      {
        path: 'bulks',
        loadComponent: () =>
          import('./pages/tables/bulks/bulks.component').then(
            (m) => m.BulksComponent
          ),
      },
      {
        path: 'sourcing',
        loadComponent: () =>
          import('./pages/tables/sourcing/sourcing.component').then(
            (m) => m.SourcingComponent
          ),
      },
      {
        path: 'protos',
        loadComponent: () =>
          import('./pages/tables/protos/protos.component').then(
            (m) => m.ProtosComponent
          ),
      },
      {
        path: 'trims',
        loadComponent: () =>
          import('./pages/tables/trims/trims.component').then(
            (m) => m.TrimsComponent
          ),
      },
      {
        path: 'print-embroidery',
        loadComponent: () =>
          import('./pages/tables/print-embroidery/print-embroidery.component').then(
            (m) => m.PrintEmbroideryComponent
          ),
      },
      {
        path: 'products',
        loadComponent: () =>
          import('./pages/tables/products/products.component').then(
            (m) => m.ProductsComponent
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/tables/settings/settings.component').then(
            (m) => m.SettingsComponent
          ),
      },
      {
        path: 'account/notifications',
        loadComponent: () =>
          import(
            './pages/account/notification-preferences/notification-preferences.component'
          ).then((m) => m.NotificationPreferencesComponent),
      },
      {
        path: 'notifications/hub',
        loadComponent: () =>
          import(
            './pages/notifications/notification-hub/notification-hub.component'
          ).then((m) => m.NotificationHubComponent),
      },
      {
        path: 'notifications/pending/:batchId',
        loadComponent: () =>
          import(
            './pages/notifications/pending-reminder-batch/pending-reminder-batch.component'
          ).then((m) => m.PendingReminderBatchComponent),
      },
    ],
  },

  // Outras rotas protegidas
  {
    path: 'invite',
    loadChildren: () =>
      import('./pages/invite/invite.routes').then((m) => m.InviteRoutes),
  },

  // Rota catch-all (deve vir por último)
  {
    path: '**',
    redirectTo: 'authentication/login',
  },
];
